import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import { LoginForm } from './LoginForm';
import { ProfileSelector } from './ProfileSelector';
import { Skeleton } from '@/components/ui/skeleton';
import { supabase } from '@/integrations/supabase/client';
import { useRoles } from '@/hooks/useRoles';
import starnightImage from '@/assets/starnight.jpeg';
import lighthouseImage from '@/assets/lighthouse.jpg';

interface Profile {
  customerNumber?: string;
  name?: string;
  company?: string;
  userName?: string;
  id?: number;
}

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, isLoading } = useAuth();
  const { isFeatureEnabled, isLoading: isFlagsLoading } = useFeatureFlags();
  const { fetchRoles } = useRoles();
  const [profileState, setProfileState] = useState<'idle' | 'loading' | 'selecting' | 'done'>(() => {
    // If a profile was already selected this session, skip directly to done
    if (sessionStorage.getItem('selectedProfile')) return 'done';
    // If user data exists in localStorage, they're already logged in — skip profile selection
    if (localStorage.getItem('user')) return 'done';
    return 'idle';
  });
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [backgroundImage] = useState(() => 
    Math.random() > 0.5 ? starnightImage : lighthouseImage
  );

  // When user logs in and useDWProfiles is enabled, fetch profiles
  useEffect(() => {
    if (!user || isFlagsLoading || profileState !== 'idle') return;

    if (!isFeatureEnabled('useDWProfiles')) {
      setProfileState('done');
      return;
    }

    // Check if profile was already selected this session
    const selectedProfile = sessionStorage.getItem('selectedProfile');
    if (selectedProfile) {
      setProfileState('done');
      return;
    }

    const cachedProfiles = sessionStorage.getItem('userProfiles');
    if (cachedProfiles) {
      try {
        const parsed = JSON.parse(cachedProfiles);
        setProfiles(parsed);
        setProfileState('selecting');
        return;
      } catch {}
    }

    const fetchProfiles = async () => {
      setProfileState('loading');
      try {
        const { data, error } = await supabase.functions.invoke('get-user-profiles', {
          body: { token: user.token }
        });

        if (!error && data?.profiles) {
          const simulatedDataMap: Record<string, any> = {
            '1067': { address: '1220 Morland Dr', city: 'Statesville', state: 'NC', zip: '28677', enabledBrands: ['Jayco', 'Keystone', 'KZ-RV', 'Thor', 'Tiffin'] },
            '003128': { address: '6700 Bruton Smith Blvd', city: 'Concord', state: 'NC', zip: '28027', enabledBrands: ['Thor', 'Jayco', 'Keystone', 'KZ-RV'] },
            '003343': { address: '114 Best Dr', city: 'Spartanburg', state: 'SC', zip: '29303', enabledBrands: ['Thor', 'Jayco', 'Keystone'] },
          };
          const enrichedProfiles = data.profiles.map((p: Profile) => ({
            ...p,
            ...(p.customerNumber && simulatedDataMap[p.customerNumber] || {}),
          }));
          sessionStorage.setItem('userProfiles', JSON.stringify(enrichedProfiles));
          setProfiles(enrichedProfiles);
          setProfileState('selecting');
        } else {
          console.error('Failed to fetch profiles:', error);
          setProfileState('done');
        }
      } catch (err) {
        console.error('Error fetching profiles:', err);
        setProfileState('done');
      }
    };

    fetchProfiles();
  }, [user, isFlagsLoading, isFeatureEnabled, profileState]);

  // Reset profile state on explicit logout (localStorage cleared)
  useEffect(() => {
    if (!user && !localStorage.getItem('user')) {
      setProfileState('idle');
      setProfiles([]);
      sessionStorage.removeItem('selectedProfile');
    }
  }, [user]);

  // Prefetch roles when profile is ready
  useEffect(() => {
    if (user && profileState === 'done') {
      fetchRoles();
    }
  }, [user, profileState, fetchRoles]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="space-y-4 w-full max-w-md">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      </div>
    );
  }

  if (!user) {
    return <LoginForm />;
  }

  // Show profile selector after login when useDWProfiles is enabled
  if (profileState === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="space-y-4 w-full max-w-md">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      </div>
    );
  }

  if (profileState === 'selecting') {
    return (
      <div key="profile-selector">
        <ProfileSelector 
          profiles={profiles} 
          backgroundImage={backgroundImage}
          onProfileSelected={() => {
            setProfileState('done');
          }}
        />
      </div>
    );
  }

  return <div key="main-content">{children}</div>;
};
